export class GameStorage {
  constructor(prefix = "klunkstone:") {
    this.prefix = prefix;
  }

  getPlayerId(createId) {
    let playerId = localStorage.getItem(`${this.prefix}playerId`);
    if (!playerId) {
      playerId = createId();
      localStorage.setItem(`${this.prefix}playerId`, playerId);
    }
    return playerId;
  }

  // Reconnecting from another device adopts the seat's player id, for good: the seat
  // is this device's identity from now on, exactly as if it had joined from here.
  setPlayerId(playerId) {
    localStorage.setItem(`${this.prefix}playerId`, playerId);
  }

  getPlayerName() {
    return localStorage.getItem(`${this.prefix}name`) || "";
  }

  setPlayerName(name) {
    localStorage.setItem(`${this.prefix}name`, name);
  }

  // The look this device plays under, stored next to the name for the same reason:
  // picking an icon is part of saying who you are, and nobody wants to pick it again
  // every time they open the game. Values are stored raw and allow-listed on the way
  // out by the controller, which is where the seed for an unpicked device lives.
  getAppearance() {
    return {
      icon: localStorage.getItem(`${this.prefix}icon`) || "",
      color: localStorage.getItem(`${this.prefix}color`) || "",
    };
  }

  setAppearance(icon, color) {
    localStorage.setItem(`${this.prefix}icon`, icon);
    localStorage.setItem(`${this.prefix}color`, color);
  }

  // Whether the phone's table strip is unfolded. A view preference rather than game
  // state, so it lives next to the name and the figure and never syncs. Folded is the
  // default: the hand is what the player acts with, and the folded strip still shows
  // every seat as a chip, so nothing is actually hidden until they ask for detail.
  getTableOpen() {
    return localStorage.getItem(`${this.prefix}tableOpen`) === "1";
  }

  setTableOpen(open) {
    localStorage.setItem(`${this.prefix}tableOpen`, open ? "1" : "0");
  }

  gameKey(code) {
    return `${this.prefix}game:${code}`;
  }

  saveLocal(game) {
    if (!game?.code) return;
    localStorage.setItem(this.gameKey(game.code), JSON.stringify(game));
  }

  loadLocal(code) {
    const raw = localStorage.getItem(this.gameKey(code));
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      // A half-written or hand-edited entry must not take the whole app down.
      this.removeLocal(code);
      return null;
    }
  }

  removeLocal(code) {
    localStorage.removeItem(this.gameKey(code));
  }

  sessionKey() {
    return `${this.prefix}session`;
  }

  // The lobby this device is currently in, kept apart from the game blob so it
  // survives a tab that gets closed, discarded by iOS, or reopened from a bookmark:
  // the URL is gone by then, and a five-letter code nobody wrote down cannot be
  // retyped. The timestamp is passed in so the controller's injected clock owns the
  // freshness rule.
  rememberSession(code, at) {
    if (!code) return;
    localStorage.setItem(this.sessionKey(), JSON.stringify({code, at}));
  }

  loadSession() {
    const raw = localStorage.getItem(this.sessionKey());
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      // Same reasoning as loadLocal: a half-written entry must not strand the device
      // on a blank screen, it just means there is nothing to resume.
      this.forgetSession();
      return null;
    }
  }

  forgetSession() {
    localStorage.removeItem(this.sessionKey());
  }

  canSync() {
    return location.protocol.startsWith("http");
  }

  async fetchRemote(code) {
    if (!this.canSync()) return null;
    try {
      const response = await fetch(`/api/games/${code}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  }

  // Joining is the one write where every phone races from the same stale snapshot, so
  // it goes through the server-side merge rather than a whole-state POST.
  async joinRemote(code, player, log) {
    if (!this.canSync()) return null;
    try {
      const response = await fetch(`/api/games/${code}/players`, {
        method: "POST",
        headers: {"content-type": "application/json"},
        body: JSON.stringify({player, log}),
      });
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  }

  // A mini-game answer goes through its own merge for the same reason a join does: every
  // phone answers at once, and whole-state POSTs would keep only the last one.
  async answerRemote(code, minigameId, playerId, answer) {
    if (!this.canSync()) return null;
    try {
      const response = await fetch(`/api/games/${code}/answers`, {
        method: "POST",
        headers: {"content-type": "application/json"},
        body: JSON.stringify({minigameId, playerId, answer}),
      });
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  }

  saveRemote(game) {
    if (!this.canSync() || !game?.code) return Promise.resolve(null);
    return fetch(`/api/games/${game.code}`, {
      method: "POST",
      headers: {"content-type": "application/json"},
      body: JSON.stringify(game),
    })
      .then((response) => response.ok ? response.json() : null)
      .catch(() => null);
  }

  saveGame(game) {
    this.saveLocal(game);
    return this.saveRemote(game).then((remoteState) => {
      if (!remoteState || remoteState.code !== game.code) return null;
      game.updatedAt = remoteState.updatedAt;
      this.saveLocal(game);
      return remoteState;
    });
  }

  sendBeaconGame(game) {
    if (!this.canSync() || !game?.code) return false;
    const body = JSON.stringify(game);
    if (navigator.sendBeacon) {
      return navigator.sendBeacon(`/api/games/${game.code}`, new Blob([body], {type: "application/json"}));
    }
    fetch(`/api/games/${game.code}`, {
      method: "POST",
      headers: {"content-type": "application/json"},
      body,
      keepalive: true,
    }).catch(() => {});
    return true;
  }
}
